import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, Loader2, Search, School, Bus, Accessibility, Warehouse, Wrench, Building2 } from "lucide-react";

/**
 * POI / Stop autocomplete backed by GET /api/logibus/poi/search.
 *
 * Differs from <AddressAutocomplete /> in that it searches inside the
 * existing transit_stops collection (= our base of truth) instead of
 * Nominatim. Useful for picking *known* stops/schools.
 *
 * Props:
 *   value              current display string (controlled)
 *   onChange(txt)      called on every keystroke
 *   onSelect(stop)     called when the user picks a suggestion. ``stop`` is
 *                      {stop_id, stop_name, stop_lat, stop_lon, type, city, address, ...}
 *   defaultType        comma-separated default type filter (e.g. "school").
 *                      When set, the dropdown shows a toggle to widen the search.
 *   placeholder
 *   testId
 *   allowToggle        show the "Élargir la recherche" toggle (default true if defaultType)
 *   emptyHint          string shown when no result for the current filter
 *
 * Visual: shows a type-coloured icon next to each suggestion + city / address
 *         hint, so an admin can pick a school by name OR by city at a glance.
 */
const TYPE_META = {
  school:    { label: "École",        Icon: School,     color: "#F59E0B" },
  stop:      { label: "Arrêt",        Icon: Bus,        color: "#3B82F6" },
  pmr:       { label: "PMR",          Icon: Accessibility, color: "#0EA5E9" },
  depot:     { label: "Dépôt",        Icon: Warehouse,  color: "#71717A" },
  workshop:  { label: "Atelier",      Icon: Wrench,     color: "#71717A" },
  client:    { label: "Client",       Icon: Building2,  color: "#10B981" },
  other:     { label: "Autre",        Icon: MapPin,     color: "#A1A1AA" },
};

export default function POIAutocomplete({
  value, onChange, onSelect,
  defaultType = "",         // ex: "school"
  placeholder,
  testId = "poi-autocomplete",
  disabled = false,
  allowToggle = true,
  emptyHint = "Aucun POI ne correspond.",
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState(defaultType);  // "" = all
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 2) { setItems([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { q, limit: 8 };
        if (typeFilter) params.type = typeFilter;
        const { data } = await api.get(`/logibus/poi/search`, { params });
        setItems(data?.items || []);
        setOpen(true);
      } catch { setItems([]); }
      finally { setLoading(false); }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [value, typeFilter]);

  const pick = (it) => {
    setOpen(false);
    setItems([]);
    onSelect?.(it);
  };

  return (
    <div ref={boxRef} className="relative" data-testid={testId}>
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
          <Search size={13} />
        </span>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={() => items.length > 0 && setOpen(true)}
          placeholder={placeholder || "Rechercher un POI…"}
          disabled={disabled}
          data-testid={`${testId}-input`}
          className="w-full border border-zinc-900 pl-7 pr-7 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFC60A] disabled:bg-zinc-50"
        />
        {loading && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400">
            <Loader2 size={13} className="animate-spin" />
          </span>
        )}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-zinc-900 shadow-[3px_3px_0_0_#0A0A0B]"
             data-testid={`${testId}-suggestions`}>
          {allowToggle && defaultType && (
            <div className="px-2 py-1.5 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between text-[10px] font-mono">
              <span className="text-zinc-600">
                Filtre :&nbsp;
                <strong>{typeFilter ? (TYPE_META[typeFilter]?.label || typeFilter) : "Tous les POI"}</strong>
              </span>
              <button
                type="button"
                onClick={() => setTypeFilter(typeFilter ? "" : defaultType)}
                data-testid={`${testId}-toggle`}
                className="border border-zinc-900 px-1.5 py-0.5 hover:bg-zinc-900 hover:text-white uppercase tracking-widest"
              >
                {typeFilter ? "Élargir la recherche" : `Limiter à ${TYPE_META[defaultType]?.label || defaultType}`}
              </button>
            </div>
          )}

          {items.length === 0 ? (
            <div className="px-3 py-3 text-xs text-zinc-500" data-testid={`${testId}-empty`}>
              {(value || "").trim().length < 2 ? "Tapez au moins 2 caractères…" : emptyHint}
            </div>
          ) : (
            items.map((it, i) => {
              const meta = TYPE_META[it.type] || TYPE_META.other;
              const Icon = meta.Icon;
              const subtitle = [it.city, it.postal_code, it.address].filter(Boolean).join(" · ");
              return (
                <button
                  key={`${it.stop_id}-${i}`}
                  type="button"
                  onClick={() => pick(it)}
                  data-testid={`${testId}-item-${i}`}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-[#FFF7D6] flex items-start gap-2 border-b border-zinc-100 last:border-b-0"
                >
                  <span
                    className="w-6 h-6 flex items-center justify-center border border-zinc-900 flex-shrink-0 mt-0.5"
                    style={{ background: meta.color, color: "#fff" }}
                    title={meta.label}
                  >
                    <Icon size={12} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="font-semibold block truncate">{it.stop_name}</span>
                    {subtitle && (
                      <span className="block text-[10px] font-mono text-zinc-500 truncate" title={subtitle}>
                        {subtitle}
                      </span>
                    )}
                  </span>
                  <span className="text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 border border-zinc-300 bg-zinc-50 flex-shrink-0 mt-0.5">
                    {meta.label}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
